import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../../components/ui/sonner";
import { api } from "../../lib/api";
import { useBranding, withAlpha } from "../../lib/branding";

export default function PortalChangePassword() {
  const { slug } = useParams();
  const nav = useNavigate();
  const branding = useBranding();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const accent = branding?.accent_color || "#FFB000";
  const clientName = branding?.name || "your portal";

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/portal/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      // Update local user flag
      const user = JSON.parse(localStorage.getItem("ra_user") || "null");
      if (user) {
        user.must_change_password = false;
        localStorage.setItem("ra_user", JSON.stringify(user));
      }
      toast.success("Password updated. Welcome aboard!");
      nav(`/portal/${slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0a0a0b] text-white/95 flex items-center justify-center px-6"
      style={{ backgroundImage: `radial-gradient(ellipse at top, ${withAlpha(accent, 0.1)}, transparent 60%)` }}
      data-testid="portal-change-password-page"
    >
      <div className="w-full max-w-[460px]">
        <div className="text-center mb-8">
          <div
            className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center border"
            style={{ background: withAlpha(accent, 0.12), borderColor: withAlpha(accent, 0.3) }}
          >
            <KeyRound className="w-6 h-6" style={{ color: accent }} strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Set your password</h1>
          <p className="text-sm text-white/50 mt-2">One-time setup. Make it strong — this protects every call and booking inside {clientName}.</p>
        </div>

        <form onSubmit={submit} className="space-y-4 bg-[#121214] border border-white/5 rounded-2xl p-6" data-testid="portal-change-password-form">
          <Field label="Current (temporary) password">
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoFocus
              data-testid="current-password-input"
              className="inp"
              placeholder="The temp password from your welcome email"
            />
          </Field>
          <Field label="New password" hint="Min 8 characters">
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={8}
              data-testid="new-password-input"
              className="inp"
            />
          </Field>
          <Field label="Confirm new password">
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              data-testid="confirm-password-input"
              className="inp"
            />
          </Field>

          <button
            type="submit"
            disabled={submitting}
            data-testid="change-password-submit-btn"
            className="w-full text-black font-semibold text-sm px-4 py-2.5 rounded-md disabled:opacity-60 flex items-center justify-center gap-2 transition-all"
            style={{ background: accent, boxShadow: `0 0 24px ${withAlpha(accent, 0.25)}` }}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {submitting ? "Updating…" : "Set password & continue"}
          </button>
        </form>
      </div>

      <style>{`
        .inp { width: 100%; padding: 10px 13px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; color: #fff; font-size: 14px; outline: none; transition: border-color .2s; }
        .inp:focus { border-color: ${accent}; }
      `}</style>
      <Toaster theme="dark" richColors position="top-right" />
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-medium text-white/65 mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
    </label>
  );
}
